import { Typography } from 'antd';
import { createStyles } from 'antd-style';
import type { CSSProperties, ReactNode } from 'react';
import UserAvatar from '../UserAvatar';
import type { UserAvatarProps } from '../UserAvatar';

const useStyles = createStyles(({ token, css }) => ({
  root: css`
    display: inline-flex;
    align-items: center;
    gap: ${token.marginXS}px;
    min-width: 0;
    max-width: 100%;
  `,
  text: css`
    min-width: 0;
    display: flex;
    flex-direction: column;
    line-height: 1.2;
  `,
  name: css`
    && {
      max-width: 100%;
      font-weight: 500;
      line-height: 1.25;
    }
  `,
  desc: css`
    && {
      max-width: 100%;
      font-size: 12px;
      line-height: 1.25;
    }
  `,
}));

export interface UserIdentityProps {
  avatar?: string;
  name?: ReactNode;
  email?: ReactNode;
  description?: ReactNode;
  size?: UserAvatarProps['size'];
  avatarProps?: Omit<UserAvatarProps, 'src' | 'name' | 'email' | 'size'>;
  className?: string;
  style?: CSSProperties;
  textStyle?: CSSProperties;
  showDescription?: boolean;
}

export default function UserIdentity({
  avatar,
  name,
  email,
  description,
  size = 32,
  avatarProps,
  className,
  style,
  textStyle,
  showDescription = true,
}: UserIdentityProps) {
  const { styles, cx } = useStyles();
  const displayName = name || email || '用户';
  const desc = description ?? email;

  return (
    <span className={cx(styles.root, className)} style={style}>
      <UserAvatar
        src={avatar}
        name={typeof displayName === 'string' ? displayName : undefined}
        email={typeof email === 'string' ? email : undefined}
        size={size}
        {...avatarProps}
      />
      <span className={styles.text} style={textStyle}>
        <Typography.Text className={styles.name} ellipsis>
          {displayName}
        </Typography.Text>
        {showDescription && desc ? (
          <Typography.Text type="secondary" className={styles.desc} ellipsis>
            {desc}
          </Typography.Text>
        ) : null}
      </span>
    </span>
  );
}
